import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, VIEW, EDIT } from "const"
import {
    replaceHistoryState,
    hasAccess,
    StateType,
} from "client/utils"
import { qidProductsRoute } from "client/parts/routes"
import {
    PRM_QID_PRODUCT,
    StoreType,
    QidProductProps,
    LoadQidProductById,
    LoadDriveFolderByQidProductId,
    setQidProductView,
    setQidProductFolderId,
    setQidModelExpandedModelIds,
    setQidModelExpandedSafetyFunctionIds,
    initQidProduct,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"

import Form from "./form"
import Models from "../models"
import Documents from "../documents"
import FailureRates from "../failureRates"
import Results from "../results"

const DETAILS = "#details"
const MODELS = "#models"
const DOCUMENTS = "#documents"
const FAILURE_RATE = "#failureRate"
const RESULTS = "#results"

const menu = [DETAILS, MODELS, DOCUMENTS, FAILURE_RATE, RESULTS]

const View = ({
    productId = null,
    hash = null,
}: QidProductProps) => {
    const product = useSelector((state: StoreType) => state.qidProduct.productView)
    const reloadView = useSelector((state: StoreType) => state.qidProduct.reloadView)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(menu.includes(hash) ? hash : DETAILS)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => (
        () => {
            // clear product on unmount
            dispatch(setQidProductView(initQidProduct))
            dispatch(setQidProductFolderId(null))
            dispatch(setQidModelExpandedModelIds([]))
            dispatch(setQidModelExpandedSafetyFunctionIds([]))
        }
    ), [])

    useEffect(() => {
        if (!reloadView) return
        setLoadState(PENDING)
        LoadQidProductById(productId).then((res1) => {
            LoadDriveFolderByQidProductId(productId).then((res2) => {
                dispatch(setQidProductView(res1))
                dispatch(setQidProductFolderId(res2.id))
                setLoadState(IDLE)
            })
        })
    }, [reloadView])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        replaceHistoryState(`${qidProductsRoute}/${VIEW}/${productId}${tab}`)
    }

    const viewAccess = hasAccess(PRM_QID_PRODUCT, VIEW)
    const editAccess = hasAccess(PRM_QID_PRODUCT, EDIT)

    if (!viewAccess) return <AccessDenied />
    if (!product) return <NotFound />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${lang.viewProduct} # ${product.id || ""}`}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === DETAILS}
                        onClick={() => handleMenuClick(DETAILS)}
                    >
                        {lang.details}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === MODELS}
                        onClick={() => handleMenuClick(MODELS)}
                    >
                        {lang.models}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === DOCUMENTS}
                        onClick={() => handleMenuClick(DOCUMENTS)}
                    >
                        {lang.documents}
                    </PageMenuButton>
                    {
                        editAccess && (
                            <PageMenuButton
                                isActive={activeTab === FAILURE_RATE}
                                onClick={() => handleMenuClick(FAILURE_RATE)}
                            >
                                {lang.givenFailureRates}
                            </PageMenuButton>
                        )
                    }
                    <PageMenuButton
                        isActive={activeTab === RESULTS}
                        onClick={() => handleMenuClick(RESULTS)}
                    >
                        {lang.results}
                    </PageMenuButton>
                </PageMenu>

                <Tab show={activeTab === DETAILS}>
                    {loadState === PENDING && <Loader />}
                    <Form />
                </Tab>
                <Tab show={activeTab === MODELS}>
                    <Models />
                </Tab>
                <Tab show={activeTab === DOCUMENTS}>
                    <Documents />
                </Tab>
                {
                    editAccess && (
                        <Tab show={activeTab === FAILURE_RATE}>
                            <FailureRates />
                        </Tab>
                    )
                }
                <Tab show={activeTab === RESULTS}>
                    <Results />
                </Tab>
            </PageContent>
        </Container>
    )
}
export default View
